import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-signup-step-two',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div [formGroup]="formGroup">
      <div class="form-group mb-3">
        <label for="companyName" class="form-label">Company Name</label>
        <input 
          type="text" 
          class="form-control" 
          id="companyName" 
          formControlName="companyName"
          placeholder="Enter your company name"
          [class.is-invalid]="formGroup.get('companyName')?.invalid && formGroup.get('companyName')?.touched"
        />
        <div class="invalid-feedback" *ngIf="formGroup.get('companyName')?.invalid && formGroup.get('companyName')?.touched">
          <span *ngIf="formGroup.get('companyName')?.errors?.['required']">Company name is required</span>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="industry" class="form-label">Industry Type</label>
        <select 
          class="form-control form-select" 
          id="industry" 
          formControlName="industry"
          [class.is-invalid]="formGroup.get('industry')?.invalid && formGroup.get('industry')?.touched"
        >
          <option value="" disabled selected>Select your industry</option>
          <option value="general-service">General Service</option>
          <option value="construction">Construction</option>
          <option value="healthcare">Healthcare</option>
          <option value="law-firm">Law Firm</option>
        </select>
        <div class="invalid-feedback" *ngIf="formGroup.get('industry')?.invalid && formGroup.get('industry')?.touched">
          <span *ngIf="formGroup.get('industry')?.errors?.['required']">Please select an industry</span>
        </div>
      </div>

      <div class="form-group mb-4">
        <label for="numberOfEmployees" class="form-label">Number of Employees</label>
        <input 
          type="number" 
          class="form-control" 
          id="numberOfEmployees" 
          formControlName="numberOfEmployees"
          placeholder="Enter number of employees"
          min="1"
          [class.is-invalid]="formGroup.get('numberOfEmployees')?.invalid && formGroup.get('numberOfEmployees')?.touched"
        />
        <div class="invalid-feedback" *ngIf="formGroup.get('numberOfEmployees')?.invalid && formGroup.get('numberOfEmployees')?.touched">
          <span *ngIf="formGroup.get('numberOfEmployees')?.errors?.['required']">Number of employees is required</span>
          <span *ngIf="formGroup.get('numberOfEmployees')?.errors?.['min']">Number must be at least 1</span>
        </div>
      </div>

      <div class="button-group">
        <button 
          type="button" 
          class="btn btn-outline-secondary"
          (click)="onBack()"
        >
          Back
        </button>
        <button 
          type="button" 
          class="btn btn-primary"
          (click)="onNext()"
          [disabled]="!isStepValid()"
        >
          Next
        </button>
      </div>
    </div>
  `,
  styles: [`
    .form-control {
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #e0e0e0;
      font-size: 14px;
    }

    .form-control:focus {
      border-color: #6a49f2;
      box-shadow: 0 0 0 0.2rem rgba(106, 73, 242, 0.15);
    }

    .form-select {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 16px 12px;
    }

    .form-label {
      font-weight: 500;
      margin-bottom: 8px;
      color: #3e3f66;
    }

    .button-group {
      display: flex;
      gap: 12px;
    }

    .btn {
      flex: 1;
      padding: 12px;
      border-radius: 8px;
      font-weight: 600;
      transition: all 0.3s;
      border: none;
    }

    .btn-primary {
      background: #6a49f2;
    }

    .btn-primary:hover:not(:disabled) {
      background: #5639d9;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(106, 73, 242, 0.3);
    }

    .btn-primary:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .btn-outline-secondary {
      background: white;
      border: 2px solid #e0e0e0;
      color: #3e3f66;
    }

    .btn-outline-secondary:hover {
      background: #f5f5f5;
      border-color: #d0d0d0;
      transform: translateY(-2px);
    }

    .is-invalid {
      border-color: #dc3545;
    }

    .invalid-feedback {
      display: block;
      color: #dc3545;
      font-size: 12px;
      margin-top: 4px;
    }
  `]
})
export class SignupStepTwoComponent {
  @Input() formGroup!: FormGroup;
  @Output() nextStep = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();

  isStepValid(): boolean {
    const companyName = this.formGroup.get('companyName');
    const industry = this.formGroup.get('industry');
    const numberOfEmployees = this.formGroup.get('numberOfEmployees');
    return !!(companyName?.valid && industry?.valid && numberOfEmployees?.valid);
  }

  onNext(): void {
    if (this.isStepValid()) {
      this.nextStep.emit();
    }
  }

  onBack(): void {
    this.previousStep.emit();
  }
}
